/**
 * OffersSection
 * Tabla de ofertas del cliente. Filtra client-side por directoryId.
 * Click en una fila abre OfferDetailModal.
 */
import { useState, useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/Badge';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { DataTable } from '@/features/credit/components/DataTable';
import { OfferDetailModal } from '@/features/credit/components/OfferDetailModal';
import { useBackofficeOffers } from '@/features/credit/hooks/useBackofficeOffers';
import { formatCurrency } from '@/utils/formatters';
import { CreditLevel } from '@/types/credit.types';
import type { BackofficeOffersListItem, AvailableRate } from '@/types/backoffice-crud.types';

interface OffersSectionProps {
  directoryId: string;
}

export function OffersSection({ directoryId }: OffersSectionProps) {
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const { offers, isLoading, isError } = useBackofficeOffers();

  const clientOffers = useMemo(
    () => offers.filter((o) => o.directory_id === directoryId),
    [offers, directoryId],
  );

  if (isLoading) return <div className="p-4 text-sm text-muted-foreground">Cargando ofertas…</div>;
  if (isError) return <div className="p-4 text-sm text-red-600 dark:text-red-400">Error al cargar ofertas.</div>;

  return (
    <div className="p-4">
      {clientOffers.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">Este cliente no tiene ofertas.</p>
      ) : (
        <DataTable
          data={clientOffers}
          columns={offerColumns}
          searchPlaceholder="Buscar por ID de oferta..."
          emptyMessage="Sin ofertas"
          pageSize={5}
          onRowClick={(o) => setSelectedOfferId(o.offer_id)}
        />
      )}

      {selectedOfferId && (
        <OfferDetailModal
          isOpen
          offerId={selectedOfferId}
          directoryId={directoryId}
          onClose={() => setSelectedOfferId(null)}
        />
      )}
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(dateStr));
  } catch { return dateStr; }
}

function truncateId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}...` : id;
}

function formatRatesSummary(rates: AvailableRate[] | undefined): string {
  if (!rates || rates.length === 0) return '-';
  const terms = rates.map((r) => r.term_weeks).sort((a, b) => a - b);
  if (terms.length === 1) return `${terms[0]} sem`;
  return `${terms[0]}-${terms[terms.length - 1]} sem`;
}

type OfferBadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getOfferStatusVariant(status: string): OfferBadgeVariant {
  switch (status) {
    case 'CONVERTED': case 'ACCEPTED': return 'success';
    case 'VIEWING': return 'info';
    case 'LOCKED': return 'warning';
    case 'EXPIRED': return 'danger';
    default: return 'default';
  }
}

const offerColumns: ColumnDef<BackofficeOffersListItem, unknown>[] = [
  { accessorKey: 'created_at', header: 'Creada', cell: ({ getValue }) => formatDateShort(getValue<string>()) },
  { accessorKey: 'offer_expires_at', header: 'Expira', cell: ({ getValue }) => formatDateShort(getValue<string>()) },
  {
    accessorKey: 'offer_id', header: 'ID',
    cell: ({ getValue }) => <span className="font-mono text-xs" title={getValue<string>()}>{truncateId(getValue<string>())}</span>,
  },
  {
    accessorKey: 'credit_level', header: 'Nivel',
    cell: ({ getValue }) => {
      const level = getValue<string>();
      const isValid = Object.values(CreditLevel).includes(level as CreditLevel);
      return isValid ? <CreditLevelBadge level={level as CreditLevel} size="sm" /> : <span className="text-xs text-muted-foreground">{level}</span>;
    },
  },
  {
    id: 'rango_monto', header: 'Rango Monto',
    cell: ({ row }) => (
      <span className="tabular-nums text-xs">
        {formatCurrency(row.original.min_amount_mxn, 'MXN', 'es-MX')} - {formatCurrency(row.original.max_amount_mxn, 'MXN', 'es-MX')}
      </span>
    ),
  },
  {
    accessorKey: 'available_rates', header: 'Plazos',
    cell: ({ row }) => <span className="text-xs tabular-nums">{formatRatesSummary(row.original.available_rates)}</span>,
  },
  {
    accessorKey: 'status', header: 'Estado',
    cell: ({ getValue }) => { const s = getValue<string>(); return <Badge variant={getOfferStatusVariant(s)}>{s}</Badge>; },
  },
];
